import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link, useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from "@/components/ui/sheet";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
    CaretLeft, Phone, Buildings, Sparkle, ArrowRight, PaperPlaneTilt,
    Cube, ClockCounterClockwise, FileText, ChatCircle, ChatCircleDots, Wallet, Info,
    ClipboardText, CheckCircle, Clock, Spinner, Plus, Trash, PencilSimple, X,
    Heart, Pill, Warning, FirstAid, WhatsappLogo,
    Tooth, Briefcase, DotsThree, Calendar as CalendarIcon, CalendarPlus,
    // iter98.99dl — Smile-goals editor icons.
    Stethoscope, Smiley, FloppyDisk,
    // iter101.62 — Icons for the DoctorWorkspaceDeck tab bar.
    Compass, Folders, NotePencil, ClockClockwise, IdentificationCard,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { confirmDialog } from "@/lib/confirm";
import { waLink, waReason } from "@/lib/wa";
import PatientBillingSection from "@/components/patient/PatientBillingSection";
import PatientNotesSection from "@/components/patient/PatientNotesSection";
import PatientAvatar from "@/components/patient/PatientAvatar";
import { AppointmentsTab } from "@/components/doctor/AppointmentsTab";
import PatientCommandHeader from "@/components/doctor/PatientCommandHeader";
import DoctorWorkspaceDeck from "@/components/doctor/DoctorWorkspaceDeck";

import IntraoralScansOverviewCard from "@/components/patient/IntraoralScansOverviewCard";
import { resizePatientPhoto } from "@/lib/resizePatientPhoto";
import RequestScanVisitModal from "@/components/patient/RequestScanVisitModal";
import { trackEvent } from "@/lib/analytics";
import { useAuth } from "@/context/AuthContext";
import PhoneInput10 from "@/components/site/PhoneInput10";
import { toE164, to10Digit } from "@/lib/phone";
import { statusLabel as sharedStatusLabel, statusTone as sharedStatusTone, isPostApproval } from "@/lib/caseStatus";

/* iter101.5 — Status pill map now delegates to the shared caseStatus lib
   so patient-profile speaks the same language as the rest of the doctor
   portal (Dashboard / Aligner Cases / Case Detail / Patients list). The
   old hardcoded STATUS_THEMES was missing `approved_pending_mfg`,
   `awaiting_production_assignment`, etc. — production was showing the
   raw "Approved Pending Mfg" underscore-case slug as the pill label. */
const TONE_TO_PILL = {
    slate:   "bg-zinc-100 text-zinc-700",
    sky:     "bg-sky-100 text-sky-800",
    violet:  "bg-violet-100 text-violet-800",
    amber:   "bg-amber-100 text-amber-800",
    rose:    "bg-rose-100 text-rose-700",
    emerald: "bg-emerald-100 text-emerald-800",
};

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const initials = (name) => (name || "?").split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

function statusTheme(status) {
    if (!status) return { label: "New patient", bg: "bg-amber-100 text-amber-800" };
    const label = sharedStatusLabel(status, "doctor") || status.replace(/_/g, " ");
    const bg = TONE_TO_PILL[sharedStatusTone(status)] || "bg-zinc-100 text-zinc-700";
    return { label, bg };
}

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }).replace(/ /g, "-");
    } catch { return iso; }
}

export default function PatientProfile() {
    const { patientId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [tab, setTab] = useState("overview");
    const [loading, setLoading] = useState(true);

    /* iter97.78 — Patient-scoped intraoral scan state.
       `scans` aggregates raw STL / PLY / OBJ + bite registration files
       across every case owned by this doctor for this patient.
       `visits` lists technician scan-visit appointments. Both are used
       by the new Overview card and the new Intraoral Scans tab. */
    const [scans, setScans] = useState([]);
    const [visits, setVisits] = useState([]);
    const [requestModalOpen, setRequestModalOpen] = useState(false);

    const loadScans = () => {
        if (!patientId) return;
        api.get(`/doctor/patients/${patientId}/scans`)
            .then((r) => setScans(r.data || []))
            .catch(() => setScans([]));
    };
    const loadVisits = () => {
        if (!patientId) return;
        api.get(`/doctor/patients/${patientId}/scan-visits`)
            .then((r) => setVisits(r.data || []))
            .catch(() => setVisits([]));
    };

    const load = () => {
        setLoading(true);
        api.get(`/doctor/patients/${patientId}`)
            .then((r) => setData(r.data))
            .catch((e) => { toast.error(e.response?.data?.detail || "Couldn't load patient"); navigate("/doctor/patients"); })
            .finally(() => setLoading(false));
        loadScans();
        loadVisits();
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [patientId]);

    // ── Photo upload (iter98.18) ────────────────────────────────────
    // Doctor clicks the patient avatar → file picker → resize to 256×256
    // square → base64 → PUT `/doctor/patients/{id}/photo`.
    const photoInputRef = useRef(null);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);

    const handlePhotoPicked = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Please pick an image file");
            return;
        }
        setUploadingPhoto(true);
        try {
            // Resize to a 256×256 cover-cropped JPEG so we stay under the
            // 250KB server cap regardless of source size.
            const dataUrl = await resizePatientPhoto(file, 256);
            const { data: resp } = await api.put(
                `/doctor/patients/${patientId}/photo`,
                { photo_data_url: dataUrl },
            );
            setData((d) => ({ ...d, photo_data_url: resp.photo_data_url }));
            toast.success("Patient photo updated");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't upload photo");
        } finally {
            setUploadingPhoto(false);
            if (photoInputRef.current) photoInputRef.current.value = "";
        }
    };

    const removePatientPhoto = async () => {
        if (!(await confirmDialog({ title: "Remove this patient's profile photo?", tone: "danger", confirmText: "Remove" }))) return;
        setUploadingPhoto(true);
        try {
            await api.put(`/doctor/patients/${patientId}/photo`, { photo_data_url: "" });
            setData((d) => ({ ...d, photo_data_url: null }));
            toast.success("Photo removed");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't remove photo");
        } finally {
            setUploadingPhoto(false);
        }
    };

    const patchVisit = async (visitId, body) => {
        // Reschedule branch — re-open the request modal with the existing
        // visit's values for now. Cleanest UX without building a 2nd modal.
        if (body && body._action === "reschedule") {
            toast.message("Reschedule", { description: "Use Cancel + Request to reschedule for now — dedicated reschedule UI lands next." });
            return;
        }
        try {
            await api.patch(`/doctor/scan-visits/${visitId}`, body);
            toast.success(body.status === "cancelled" ? "Visit cancelled" : "Visit updated");
            loadVisits();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't update visit");
        }
    };

    const onDownloadFile = (f) => {
        if (!f?.case_id || !f?.file_id) return;
        const url = `${(process.env.REACT_APP_BACKEND_URL || "")}/api/cases/${f.case_id}/files/${f.file_id}`;
        window.open(url, "_blank", "noopener");
    };

    if (loading || !data) {
        return (
            <div className="text-center text-muted-foreground py-16 text-sm">
                <Spinner size={20} className="animate-spin inline-block mr-2" /> Loading patient…
            </div>
        );
    }

    const ageStr = data.age ? `${data.age} years` : null;
    /* iter98.99dn — Resolve the canonical gender value to its display
       label (handles both new snake_case values and legacy capitalized
       values without showing junk like "non_binary"). */
    const genderStr = (() => {
        if (!data.gender) return null;
        const normalised = normaliseGender(data.gender);
        const opt = GENDER_OPTIONS.find((o) => o.value === normalised);
        return opt ? (opt.value === "" ? null : opt.label) : data.gender;
    })();
    const idStr = data.human_id || `INV-PT-${(data.id || "").slice(0, 6).toUpperCase()}`;
    const headerMeta = [ageStr, genderStr, `ID: ${idStr}`].filter(Boolean).join(", ");

    return (
        <div data-testid="patient-profile-page" className="space-y-6">
            {/* BREADCRUMB */}
            <Link to="/doctor/patients" className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground">
                <CaretLeft size={12} weight="bold" /> All patients
            </Link>

            {/* HEADER — iter101.59 · shared "command bar" grammar
                (see /components/shared/CommandHeaderShell). Same
                editorial identity band + 6-cell chip strip the Doctor
                + Planner see on Case Detail. Photo upload lives inside
                the PatientCommandHeader's avatar slot. */}
            <PatientCommandHeader
                patient={data}
                photoInputRef={photoInputRef}
                uploadingPhoto={uploadingPhoto}
                onRemovePhoto={removePatientPhoto}
                onBookScanVisit={() => {
                    trackEvent("scan_visit_button_clicked", {
                        patient_id: data?.id,
                        source: "patient_header",
                    });
                    setRequestModalOpen(true);
                }}
            />
            {/* Hidden input mounted here since the photo button lives
                inside the header component — keeps the input element
                inside the same subtree PatientProfile owns. */}
            <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handlePhotoPicked(e.target.files?.[0])}
                data-testid="patient-photo-input"
            />

            {/* TABS — iter98.99dp: dedicated "Intraoral Scans" tab removed.
                Scans are already captured per-case (the case workspace is
                where they're clinically relevant). The Overview still
                shows the IntraoralScansOverviewCard when scans/visits
                exist so the doctor sees scan activity at a glance, and
                a scan visit can still be requested from the same card.
                Removing the standalone tab eliminated the duplicated
                surface that pulled the same scans the case workspace
                already shows. */}
            {/* iter101.62 — Editorial tab bar (DoctorWorkspaceDeck).
                Same visual grammar as PlannerWorkspaceDeck: icon disc
                + label pill, teal accents, active-state gradient. */}
            <DoctorWorkspaceDeck
                tabs={[
                    { id: "overview",     label: "Overview",     Icon: Compass,             hint: "Quick summary of the patient" },
                    { id: "cases",        label: "Cases",        Icon: Folders,             hint: "Aligner cases for this patient", badge: data.cases.length > 0 ? String(data.cases.length) : null },
                    { id: "appointments", label: "Appointments", Icon: CalendarIcon,        hint: "Scheduled scan visits and follow-ups" },
                    { id: "notes",        label: "Notes",        Icon: NotePencil,          hint: "Your private clinical journal" },
                    { id: "activity",     label: "Activity",     Icon: ClockClockwise,      hint: "Event log across every case" },
                    { id: "billing",      label: "Billing",      Icon: Wallet,              hint: "Invoices, payments, and outstanding balance" },
                    { id: "about",        label: "Profile",      Icon: IdentificationCard,  hint: "Editable profile details" },
                ]}
                activeTab={tab}
                onTabChange={setTab}
                testIdPrefix="patient-tab"
                listTestId="patient-tabs"
            />

            {/* TAB CONTENT */}
            {tab === "overview" && (
                <OverviewTab
                    data={data}
                    onSubmitCase={() => navigate(`/doctor/cases/new?patient_id=${data.id}`)}
                    onRefresh={load}
                    onGoToAbout={() => setTab("about")}
                    scans={scans}
                    visits={visits}
                    onRequestVisit={() => setRequestModalOpen(true)}
                    onViewAllScans={() => setTab("scans")}
                />
            )}
            {tab === "cases" && (
                <CasesTab
                    cases={data.cases}
                    onCreateCase={() => navigate(`/doctor/cases/new?patient_id=${data.id}`)}
                />
            )}
            {tab === "appointments" && (
                <AppointmentsTab patientId={data.id} patientName={data.name} />
            )}
            {tab === "notes" && (
                <NotesTab data={data} />
            )}
            {tab === "activity" && (
                <ActivityTab data={data} />
            )}
            {tab === "billing" && (
                <BillingTab data={data} />
            )}
            {tab === "about" && (
                <DetailsTab patient={data} onSaved={load} />
            )}

            {/* iter97.78 — Request scan visit modal (rendered once at
                root so both the Overview card and the Intraoral Scans
                tab open the same instance). iter97.97 — Now always
                creates a case + captures payment; reload patient on
                success so the new case appears in Recent cases. */}
            <RequestScanVisitModal
                open={requestModalOpen}
                onClose={() => setRequestModalOpen(false)}
                onCreated={() => { load(); }}
                patient={{ id: data.id, name: data.name }}
            />
        </div>
    );
}

/* ---------------- Tab: Overview ---------------- */
function OverviewTab({ data, onSubmitCase, onRefresh, onGoToAbout, scans = [], visits = [], onRequestVisit, onViewAllScans }) {
    const hasActiveCase = data.cases.some((c) => !["completed", "cancelled"].includes(c.status));
    const showInvite = !data.has_signed_in;

    // Aligner-progress tracker removed from the Overview tab on 2026-02-27.
    // It lives on each case workspace where the "what stage are they on?"
    // question is answered in case context.

    // Lightweight billing pulse — outstanding balance + last-invoice date.
    // One API call, no card chrome, links to the Billing tab. Doctors get
    // an at-a-glance answer to "does this patient owe me anything?".
    const [billingPulse, setBillingPulse] = useState(null);
    useEffect(() => {
        if (!data?.id) return;
        api.get("/doctor/invoices", { params: { patient_id: data.id } })
            .then((r) => {
                const live = (r.data || []).filter((i) => i.status !== "void");
                const outstanding = live.reduce((s, i) => s + (i.balance || 0), 0);
                const last = live.reduce((acc, i) => {
                    const ts = i.issued_at || i.created_at;
                    return ts && (!acc || ts > acc) ? ts : acc;
                }, null);
                setBillingPulse({ count: live.length, outstanding, last });
            })
            .catch(() => setBillingPulse({ count: 0, outstanding: 0, last: null }));
    }, [data?.id]);

    return (
        <div className="space-y-4">
            {/* iter97.86 — Overview tab now uses the full available width
                (was `max-w-3xl`). Both Intraoral Scans + Recent Cases
                cards are vertical and benefit from breathing room — the
                wider container also lines the page up with the Cases
                and Appointments tabs which were already full-width. */}

            {/* iter98.99dn — Smile Goals capture moved to the About tab
                (where all other patient-detail fields live). Overview
                only DISPLAYS the goals in a compact glanceable strip —
                no inline editor here so the page stays scannable. The
                empty state and the filled card both link to the About
                tab for editing. */}
            <SmileGoalsIntakeCard
                goals={data.smile_goals}
                onEdit={onGoToAbout}
            />

            {/* iter98.18 — Overview tab no longer surfaces empty-state
                "Capture intraoral scans" / "Submit a new case" prompts.
                When the patient has neither scans nor cases the Overview
                tab still shows the at-a-glance billing line, nudges and
                cases summary; the doctor uses the dedicated Intraoral
                Scans tab / Cases tab to start those flows. */}
            {/* When the patient already HAS scans or a scheduled visit,
                the IntraoralScansOverviewCard still surfaces the recent
                scan list — useful at-a-glance info, not an empty CTA. */}
            {(scans.length > 0 || (visits && visits.some((v) => v.status === "scheduled"))) && (
                <IntraoralScansOverviewCard
                    scans={scans}
                    visits={visits}
                    onRequestVisit={onRequestVisit}
                    onViewAll={onViewAllScans}
                />
            )}

            {/* Secondary CTA — invite */}
            {showInvite && (
                <CtaCard
                    tone="muted"
                    title="Invite patient to Smile App"
                    body="Patient hasn't signed in yet. Send an SMS / WhatsApp link so they can track aligner progress, log wear-time and chat with you."
                    cta="Send invite"
                    Icon={PaperPlaneTilt}
                    ctaDisabled
                    ctaHint="Coming with the patient invite integration"
                    testid="overview-invite-patient-cta"
                />
            )}

            {/* Aligner Progress tracker removed from the Overview tab on
                2026-02-27 — it lives on each case workspace where the
                "what stage are they on?" question is naturally answered
                in context. Surfacing it on the patient overview duplicated
                state and felt loud. */}

            {/* Billing micro-stat — quiet at-a-glance line, no card chrome. */}
            {billingPulse && billingPulse.count > 0 && (
                <BillingPulse pulse={billingPulse} />
            )}

            {/* iter97.99 — Nudges surfaced on the Overview tab. Was buried
                inside its own tab; the doctor's most common patient-level
                action ("ping them on WhatsApp about wear-time") lives one
                click away now. iter98.0 — Nudges tab folded into this
                card to reduce tab clutter; full history expands inline. */}
            <PatientNudgesOverviewCard
                patientId={data.id}
                patientName={data.name}
                patientPhone={data.phone}
                latestCase={data.cases?.[0] || null}
            />

            {/* Latest cases preview */}
            {data.cases.length > 0 && (
                <div className="rounded-2xl border border-border bg-card p-5">
                    <div className="flex items-center justify-between mb-3">
                        <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Recent cases</div>
                        <Link to="/doctor/cases" className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1">
                            View all <ArrowRight size={11} weight="bold" />
                        </Link>
                    </div>
                    <div className="divide-y divide-border">
                        {data.cases.slice(0, 3).map((c) => (
                            <CasePreviewRow key={c.id} c={c} />
                        ))}
                    </div>
                </div>
            )}
            {/* Recent activity intentionally removed from the Overview rail —
                the dedicated Activity tab is the single source of truth. */}
        </div>
    );
}

/* ---------------- Tab: Cases ---------------- */
function CasesTab({ cases, onCreateCase }) {
    if (cases.length === 0) {
        return (
            <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center">
                <Cube size={28} className="mx-auto text-muted-foreground mb-3" />
                <div className="font-display text-lg font-bold">No cases yet</div>
                <p className="text-xs text-muted-foreground mt-1.5 max-w-md mx-auto">
                    When you submit a new aligner case for this patient it&apos;ll show up here with its full plan and shipping history.
                </p>
                {onCreateCase && (
                    <Button
                        size="sm"
                        onClick={onCreateCase}
                        className="rounded-full px-5 mt-5"
                        data-testid="cases-tab-create-empty-btn"
                    >
                        <Plus size={13} weight="bold" className="mr-1.5" /> Start new case
                    </Button>
                )}
            </div>
        );
    }
    return (
        <div className="space-y-3">
            {/* iter101.90 — "New case" button removed per user direction.
                Doctors can still start a new case from the header-level
                CTA (Book scan visit / New case row) and the empty-state
                variant above. Keeping a redundant CTA both above the
                table and in the header cluttered the tab. */}
            <div className="flex items-center justify-between gap-3">
                <div className="text-xs text-muted-foreground">
                    <span className="font-semibold text-foreground tabular-nums">{cases.length}</span> case{cases.length === 1 ? "" : "s"} on file
                </div>
            </div>

            <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="patient-cases-tab">
                <div className="grid grid-cols-12 gap-3 px-5 py-3 text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground border-b border-border bg-secondary/30">
                    <div className="col-span-3">Case</div>
                    <div className="col-span-3">Diagnosis</div>
                    <div className="col-span-2">Status</div>
                    <div className="col-span-2">Plan</div>
                    <div className="col-span-2 text-right">Created</div>
                </div>
                {cases.map((c) => (
                    <PatientCaseRow key={c.id} c={c} />
                ))}
            </div>
        </div>
    );
}

function planAtAGlance(plan) {
    if (!plan) return null;
    const hasDeliverable = !!(plan.external_link || plan.video_file_id);
    const hasMetrics = !!(plan.aligner_count_upper || plan.aligner_count_lower || plan.estimated_duration_weeks || plan.total_ipr_mm);
    const hasProtocol = !!(plan.proposed_attachments || plan.proposed_ipr || plan.proposed_elastics || plan.planner_notes);
    if (!hasDeliverable && !hasMetrics && !hasProtocol) return null;
    return { hasDeliverable, hasMetrics, hasProtocol };
}

function PatientCaseRow({ c }) {
    const theme = statusTheme(c.status);
    const summary = planAtAGlance(c.plan);
    const hasPlan = !!summary;
    return (
        <Link
            to={`/doctor/cases/${c.id}`}
            className="block border-b border-border last:border-0 hover:bg-secondary/30 transition-colors"
            data-testid={`patient-case-row-${c.id}`}
        >
            <div
                className="w-full grid grid-cols-12 gap-3 px-5 py-4 text-sm items-center"
                data-testid={`patient-case-link-${c.id}`}
            >
                <div className="col-span-3 font-mono font-semibold text-primary truncate">{c.code || "—"}</div>
                <div className="col-span-3 truncate text-foreground/85">{c.diagnosis || "—"}</div>
                <div className="col-span-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-md font-bold uppercase tracking-wider text-[9px] ${theme.bg}`}>{theme.label}</span>
                </div>
                <div className="col-span-2 text-xs">
                    {hasPlan ? (
                        <span className="inline-flex items-center gap-1 text-primary font-medium">
                            <ClipboardText size={11} weight="fill" /> View plan
                        </span>
                    ) : isPostApproval(c.status) ? (
                        /* iter101.5 — Post-approval cases where the plan
                           blob happens to be sparse still count as
                           "approved" — the doctor already signed off.
                           Saying "Awaiting plan" here contradicted the
                           status pill next to it (was reported in
                           production as bug #3). */
                        <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">
                            <CheckCircle size={11} weight="fill" /> Plan approved
                        </span>
                    ) : (
                        <span className="text-muted-foreground italic">Awaiting plan</span>
                    )}
                </div>
                <div className="col-span-2 text-xs text-muted-foreground text-right tabular-nums">{fmtDate(c.created_at)}</div>
            </div>
        </Link>
    );
}

function PlanAtAGlance({ plan, planVideo }) {
    const fmtMm = (v) => (v === null || v === undefined || v === "" ? "—" : `${v} mm`);
    return (
        <div className="bg-white rounded-xl border border-border p-4 space-y-3 mt-1" data-testid="patient-plan-at-a-glance">
            {/* Deliverable */}
            {(plan.external_link || planVideo) && (
                <div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1.5">3rd-party deliverable</div>
                    <div className="space-y-1">
                        {plan.external_link && (
                            <a href={plan.external_link} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-sm text-violet-700 hover:underline break-all"
                                data-testid="patient-plan-link"
                            >
                                <Sparkle size={12} weight="fill" /> {plan.external_link}
                            </a>
                        )}
                        {planVideo && (
                            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${planVideo.id}?auth=${localStorage.getItem("ca_token")}`}
                                target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-sm text-violet-700 hover:underline"
                            >
                                <FileText size={12} /> {planVideo.name}
                            </a>
                        )}
                    </div>
                </div>
            )}

            {/* Metrics */}
            {(plan.aligner_count_upper || plan.aligner_count_lower || plan.estimated_duration_weeks || plan.total_ipr_mm) && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                    <Metric label="Upper aligners" value={plan.aligner_count_upper ?? "—"} />
                    <Metric label="Lower aligners" value={plan.aligner_count_lower ?? "—"} />
                    <Metric label="Duration" value={plan.estimated_duration_weeks ? `${plan.estimated_duration_weeks} wks` : "—"} />
                    <Metric label="Total IPR" value={fmtMm(plan.total_ipr_mm)} />
                </div>
            )}

            {/* Bite metrics */}
            {(plan.initial_overjet_mm || plan.final_overjet_mm || plan.initial_overbite_mm || plan.final_overbite_mm) && (
                <div className="grid grid-cols-2 gap-2 text-xs">
                    <Metric label="Overjet (initial → final)" value={`${fmtMm(plan.initial_overjet_mm)} → ${fmtMm(plan.final_overjet_mm)}`} />
                    <Metric label="Overbite (initial → final)" value={`${fmtMm(plan.initial_overbite_mm)} → ${fmtMm(plan.final_overbite_mm)}`} />
                </div>
            )}

            {/* Protocol */}
            {plan.proposed_attachments && <ProtocolField label="Attachments" value={plan.proposed_attachments} />}
            {plan.proposed_ipr && <ProtocolField label="IPR plan" value={plan.proposed_ipr} />}
            {plan.proposed_elastics && <ProtocolField label="Elastics" value={plan.proposed_elastics} />}
            {plan.planner_notes && <ProtocolField label="Planner notes" value={plan.planner_notes} italic />}
        </div>
    );
}

function Metric({ label, value }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="font-medium tabular-nums">{value}</div>
        </div>
    );
}

function ProtocolField({ label, value, italic }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">{label}</div>
            <p className={`text-sm whitespace-pre-line ${italic ? "italic text-foreground/80" : ""}`}>{value}</p>
        </div>
    );
}

/* ---------------- Tab: Billing (patient-scoped invoice ledger) ----------------
   Invoices are issued by the doctor's clinic to the patient, optionally
   linked to one of the patient's cases. */
function BillingTab({ data }) {
    return (
        // iter101.82 — Removed max-w-4xl so the tab fills the outer
        // container (which already widens dynamically via
        // useOutletContext when the sidebar is collapsed).
        <div>
            <PatientBillingSection
                patientId={data.id}
                patientName={data.name}
                cases={data.cases || []}
            />
        </div>
    );
}

/* ---------------- Tab: Notes & Activity (merged) ----------------
 * iter97.86 — Notes (doctor-authored clinical notes) and Activity
 * (auto-generated case status events) used to live in two separate
 * tabs. Merged into one so doctors have a SINGLE place to look when
 * answering "what's happened with this patient?". Both sections sit
 * vertically — Notes on top because that's where the doctor's own
 * input lives, Activity below for at-a-glance status history.
 */
/* iter98.99dp — Notes & Activity split into two independent tabs.
   `NotesTab` is purely the doctor-authored clinical notes section,
   `ActivityTab` is the auto-generated case status timeline. Keeping
   them as separate components (instead of conditional rendering inside
   one tab) makes the page-level intent self-documenting and lets us
   evolve either surface independently. */
function NotesTab({ data }) {
    const { user } = useAuth();
    return (
        // iter101.82 — Full-width tab (removed max-w-4xl).
        <div className="space-y-6" data-testid="patient-notes-tab">
            <PatientNotesSection
                patientId={data.id}
                patientName={data.name}
                cases={data.cases || []}
                currentUser={user}
            />
        </div>
    );
}

function ActivityTab({ data }) {
    const activity = data.activity || [];
    return (
        // iter101.82 — Full-width tab (removed max-w-4xl).
        <div className="space-y-6" data-testid="patient-activity-tab">
            <div>
                <div className="flex items-center gap-2 mb-2.5">
                    <span className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Case activity</span>
                    <div className="flex-1 h-px bg-border" />
                </div>
                {activity.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-border bg-card p-8 text-center">
                        <ClockCounterClockwise size={24} className="mx-auto text-muted-foreground mb-2" />
                        <div className="font-display text-sm font-bold">No activity yet</div>
                        <p className="text-xs text-muted-foreground mt-1.5">Events from every case under this patient will be listed here.</p>
                    </div>
                ) : (
                    <div className="rounded-2xl border border-border bg-card p-5 space-y-3">
                        {activity.map((e, idx) => (
                            <ActivityEntry key={`${e.case_id}-${e.at}-${idx}`} e={e} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}


/* ---------------- Sidebar: Recent Activity rail ---------------- */
function ActivityEntry({ e, compact = false }) {
    const theme = statusTheme(e.to_status);
    return (
        <div className={`relative ${compact ? "" : "p-3 rounded-xl bg-secondary/30"}`}>
            <div className={`flex items-baseline justify-between gap-2 ${compact ? "" : "mb-1"}`}>
                <div className="text-xs font-semibold text-foreground/85 truncate">
                    <span className="font-mono text-primary">{e.case_code}</span>
                    <span className="ml-1.5">{statusTheme(e.from_status).label} → {theme.label}</span>
                </div>
                <div className="text-[10px] text-muted-foreground tabular-nums shrink-0">{fmtDate(e.at)}</div>
            </div>
            {(e.actor_name || e.actor_role) && (
                <div className="text-[10px] text-muted-foreground">
                    by {e.actor_name || e.actor_role}{e.actor_role ? ` (${e.actor_role})` : ""}
                </div>
            )}
            {e.comment && <div className="text-xs text-muted-foreground italic mt-0.5">"{e.comment}"</div>}
        </div>
    );
}

function CasePreviewRow({ c }) {
    const theme = statusTheme(c.status);
    return (
        <Link
            to={`/doctor/cases/${c.id}`}
            className="flex items-center justify-between py-3 first:pt-0 last:pb-0 gap-3 -mx-2 px-2 rounded-lg hover:bg-secondary/40 transition-colors"
            data-testid={`overview-case-${c.id}`}
        >
            <div className="min-w-0">
                <div className="text-sm font-semibold font-mono text-primary">{c.code}</div>
                <div className="text-xs text-muted-foreground truncate">{c.diagnosis || "—"}</div>
            </div>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-md font-bold uppercase tracking-wider text-[9px] shrink-0 ${theme.bg}`}>
                {theme.label}
            </span>
        </Link>
    );
}


/* ---------------- Billing micro-stat ----------------
   Quiet single-line summary of the patient's billing health. No chrome —
   sits between the major Overview cards as an editorial whisper.
   Renders only when there's at least one non-void invoice on file. */
function BillingPulse({ pulse }) {
    const fmt = (n) => new Intl.NumberFormat("en-IN", {
        style: "currency", currency: "INR", maximumFractionDigits: 0,
    }).format(n || 0);
    const lastIso = pulse.last;
    const lastLabel = lastIso
        ? new Date(lastIso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
        : null;
    const owesSomething = (pulse.outstanding || 0) > 0;
    return (
        <Link
            to="?tab=billing"
            onClick={(e) => {
                // The tab strip drives off `tab` state, not URL query — so we
                // intercept the click and dispatch a synthetic tab switch by
                // clicking the Billing tab button. Tiny but works without a
                // full URL-routing refactor of PatientProfile.
                e.preventDefault();
                const btn = document.querySelector("[data-testid='patient-tab-billing']")
                    || Array.from(document.querySelectorAll("button")).find((b) => b.textContent?.trim() === "Billing");
                btn?.click();
            }}
            className="block rounded-2xl border border-border bg-secondary/20 px-5 py-4 hover:bg-secondary/40 transition-colors group"
            data-testid="overview-billing-pulse"
        >
            <div className="flex items-baseline justify-between gap-4">
                <div className="text-sm">
                    {owesSomething ? (
                        <>
                            <span className="font-display text-lg font-medium tabular-nums text-rose-700">{fmt(pulse.outstanding)}</span>
                            <span className="text-muted-foreground italic"> outstanding</span>
                        </>
                    ) : (
                        <>
                            <span className="font-medium text-emerald-700">All invoices settled</span>
                            <span className="text-muted-foreground italic"> · no balance</span>
                        </>
                    )}
                    {lastLabel && (
                        <>
                            <span className="text-muted-foreground"> · </span>
                            <span className="text-muted-foreground">last invoice {lastLabel}</span>
                        </>
                    )}
                </div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-primary group-hover:underline">
                    Open Billing →
                </span>
            </div>
        </Link>
    );
}


/* ---------------- CTA card primitive ---------------- */
function CtaCard({ tone = "amber", title, body, cta, Icon, onClick, to, ctaDisabled, ctaHint, testid }) {
    const tones = {
        amber:   "bg-amber-50 border-amber-200",
        primary: "bg-primary/5 border-primary/30",
        muted:   "bg-card border-border",
    };
    const btnTone = {
        amber:   "bg-amber-600 hover:bg-amber-700 text-white",
        primary: "bg-primary hover:bg-primary/90 text-white",
        muted:   "bg-secondary text-foreground/70 hover:bg-secondary",
    };

    const Btn = (
        <Button
            size="sm"
            disabled={ctaDisabled}
            onClick={onClick}
            className={`rounded-full px-4 ${btnTone[tone]}`}
            data-testid={`${testid}-btn`}
        >
            {cta} <ArrowRight size={12} weight="bold" className="ml-1.5" />
        </Button>
    );

    return (
        <div className={`relative rounded-2xl border ${tones[tone]} p-5`} data-testid={testid}>
            <div className="flex items-start gap-4">
                <div className="shrink-0 w-10 h-10 rounded-full bg-white border border-current/10 grid place-items-center text-current/70">
                    <Icon size={18} weight="bold" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-display text-base font-bold leading-tight">{title}</div>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{body}</p>
                    {ctaHint && <div className="text-[10px] text-muted-foreground italic mt-1">{ctaHint}</div>}
                </div>
                <div className="shrink-0">
                    {to ? <Link to={to}>{Btn}</Link> : Btn}
                </div>
            </div>
        </div>
    );
}

/* ---------------- Smile Goals intake card ----------------
 * iter98.41 — Surfaces the 4-question onboarding survey the patient
 * fills on first portal visit post-booking. Sits at the top of the
 * Overview tab so the doctor walks into the consult already knowing
 * the primary goal, timeline, concerns, and prior ortho history.
 *
 * `goals` shape (from GET /doctor/patients/{id}):
 *   {
 *     primary_goal: "aesthetics" | "functional_bite" | "confidence" | "other" | null,
 *     primary_goal_other: string,
 *     concerns: string,
 *     timeline: "no_rush" | "within_6m" | "within_year" | "specific_event" | null,
 *     timeline_event_date: ISO date string,
 *     prior_treatment: bool | null,
 *     prior_treatment_notes: string,
 *     completed_at: ISO string,
 *     updated_at: ISO string,
 *   }
 *
 * When the patient hasn't submitted anything (`goals` null or empty),
 * we still render a quiet "not shared yet" card so the absence of data
 * is visible — the doctor can ask about it at the visit.
 */
const SMILE_GOAL_META = {
    aesthetics:      { label: "Aesthetics",      Icon: Sparkle,    tint: "bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200" },
    functional_bite: { label: "Functional bite", Icon: Tooth,      tint: "bg-cyan-50 text-cyan-700 ring-cyan-200" },
    confidence:      { label: "Confidence",      Icon: Briefcase,  tint: "bg-amber-50 text-amber-700 ring-amber-200" },
    other:           { label: "Something else",  Icon: DotsThree,  tint: "bg-slate-50 text-slate-700 ring-slate-200" },
};
const SMILE_TIMELINE_META = {
    no_rush:        "No rush — whenever's right",
    within_6m:      "Within 6 months",
    within_year:    "Within a year",
    specific_event: "Has a specific event",
};

function SmileGoalsIntakeCard({ goals, onEdit }) {
    /* iter98.99dn — Compact, glanceable, single-rhythm strip on Overview.
       Capture / edit lives on the About tab (where the rest of the
       patient-detail fields are managed). Four pieces of info packed
       into one row: primary goal chip · timeline chip · prior-ortho
       chip · the concerns one-liner. Empty state is a slim CTA strip
       pointing to the About tab. */
    const hasAnyData = goals && (
        goals.primary_goal
        || goals.concerns
        || goals.timeline
        || (goals.prior_treatment !== null && goals.prior_treatment !== undefined)
    );
    const enteredByDoctor = goals?.source === "doctor";

    if (!hasAnyData) {
        return (
            <div
                className="rounded-xl border border-dashed border-border bg-card px-4 py-2.5 flex items-center gap-3"
                data-testid="patient-smile-goals-empty"
            >
                <Smiley size={14} weight="duotone" className="text-muted-foreground/80 shrink-0" />
                <span className="flex-1 text-[12.5px] text-muted-foreground">
                    Smile goals not captured yet — capture them in <span className="font-semibold text-foreground/85">Profile</span> so they&apos;re ready when the patient logs in.
                </span>
                {onEdit && (
                    <button
                        type="button"
                        onClick={onEdit}
                        className="text-[11.5px] font-semibold text-primary hover:underline inline-flex items-center gap-1 shrink-0"
                        data-testid="smile-goals-capture-cta"
                    >
                        Capture in Profile <ArrowRight size={11} weight="bold" />
                    </button>
                )}
            </div>
        );
    }

    const goalKey = goals.primary_goal || "other";
    const goalMeta = SMILE_GOAL_META[goalKey] || SMILE_GOAL_META.other;
    const GoalIcon = goalMeta.Icon;
    const eventDate = goals.timeline_event_date
        ? new Date(goals.timeline_event_date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })
        : null;
    /* Compose the timeline label so the chip reads naturally:
       - generic: "Within 6 months"
       - specific event: "Wedding · 12 Aug" (name + date) or just the name if no date */
    let timelineChipLabel = null;
    if (goals.timeline === "specific_event") {
        const nm = (goals.timeline_event_name || "").trim();
        if (nm && eventDate)      timelineChipLabel = `${nm} · ${eventDate}`;
        else if (nm)              timelineChipLabel = nm;
        else if (eventDate)       timelineChipLabel = `Event: ${eventDate}`;
        else                      timelineChipLabel = SMILE_TIMELINE_META.specific_event;
    } else if (goals.timeline) {
        timelineChipLabel = SMILE_TIMELINE_META[goals.timeline] || goals.timeline;
    }
    const hasPriorOrtho = goals.prior_treatment === true;
    const noPriorOrtho  = goals.prior_treatment === false;
    /* iter98.99do — Build the chip-group list so we can drive a single
       grid layout and avoid the previous unlabelled chip-soup that
       gave the doctor no way to tell which chip was which. */
    const groups = [];
    if (goals.primary_goal) {
        groups.push({
            id: "primary",
            label: "Primary goal",
            chip: (
                <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold ring-1 text-[11.5px] ${goalMeta.tint}`}
                    title={goals.primary_goal === "other" && goals.primary_goal_other ? goals.primary_goal_other : goalMeta.label}
                >
                    <GoalIcon size={10} weight="fill" /> {goalMeta.label}
                </span>
            ),
            testid: "smile-goals-primary",
        });
    }
    if (timelineChipLabel) {
        groups.push({
            id: "timeline",
            label: "Timeline",
            chip: (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold bg-primary/10 text-primary ring-1 ring-primary/20 text-[11.5px] max-w-[200px] truncate">
                    <CalendarIcon size={10} weight="fill" /> <span className="truncate">{timelineChipLabel}</span>
                </span>
            ),
            testid: "smile-goals-timeline",
        });
    }
    if (hasPriorOrtho || noPriorOrtho) {
        groups.push({
            id: "prior",
            label: "Prior ortho",
            chip: hasPriorOrtho ? (
                <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold bg-amber-50 text-amber-800 ring-1 ring-amber-200 text-[11.5px]"
                    title={goals.prior_treatment_notes || "Prior ortho treatment"}
                >
                    <Warning size={10} weight="fill" /> Had braces/aligners
                </span>
            ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium bg-secondary text-muted-foreground text-[11.5px]">
                    First-time patient
                </span>
            ),
            testid: "smile-goals-prior",
        });
    }

    return (
        <div
            className="rounded-xl border border-border bg-card px-4 py-3 flex items-start gap-3"
            data-testid="patient-smile-goals-card"
        >
            <div className="w-8 h-8 rounded-full bg-primary/10 grid place-items-center shrink-0 mt-0.5">
                <Smiley size={15} weight="fill" className="text-primary" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                    <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                        Smile Goals
                    </span>
                    {/* Attribution chip — kept tiny so it doesn't compete with the data */}
                    {enteredByDoctor ? (
                        <span
                            className="inline-flex items-center gap-0.5 text-[9.5px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-50 text-violet-700"
                            title={goals.captured_by_name ? `Captured by ${goals.captured_by_name}` : "Captured by your team"}
                            data-testid="smile-goals-attribution-doctor"
                        >
                            <Stethoscope size={9} weight="duotone" /> {goals.captured_by_name ? `By ${(goals.captured_by_name.split(" ").slice(-2).join(" "))}` : "By team"}
                        </span>
                    ) : goals.completed_at && (
                        <span
                            className="inline-flex items-center gap-0.5 text-[9.5px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700"
                            data-testid="smile-goals-attribution-patient"
                        >
                            <CheckCircle size={9} weight="fill" /> By patient
                        </span>
                    )}
                </div>
                {/* iter98.99do — Labelled chip groups so the doctor can
                    tell at a glance which chip means what. Previous
                    iteration showed bare chips with no headers — a
                    cardinal sin per the user feedback. */}
                <div className="grid grid-cols-3 gap-x-4 gap-y-1.5">
                    {groups.map((g) => (
                        <div key={g.id} className="min-w-0" data-testid={g.testid}>
                            <div className="text-[9.5px] font-bold uppercase tracking-[0.16em] text-muted-foreground/85 mb-1 truncate">
                                {g.label}
                            </div>
                            {g.chip}
                        </div>
                    ))}
                </div>
                {goals.concerns && (
                    <div
                        className="text-[12px] text-muted-foreground italic mt-2.5 leading-relaxed line-clamp-1"
                        title={goals.concerns}
                        data-testid="smile-goals-concerns"
                    >
                        &ldquo;{goals.concerns}&rdquo;
                    </div>
                )}
            </div>
        </div>
    );
}


/* DoctorSmileGoalsEditor — modal lets the doctor capture / edit the
   four smile-goals fields on behalf of a patient. Mirrors the patient-
   side onboarding modal copy/options so the saved record looks identical
   regardless of who entered it. iter98.99dl. */
function DoctorSmileGoalsEditor({ patientId, patientName, initial, onSaved }) {
    /* iter98.99dn — Was a Dialog; now an inline form rendered as a
       SectionCard inside the About tab. Removes the modal interaction
       cost — the doctor edits in place alongside the rest of the
       patient-detail fields (Personal, Medical, Notes). */
    const [primaryGoal, setPrimaryGoal] = useState(initial?.primary_goal || "");
    const [primaryGoalOther, setPrimaryGoalOther] = useState(initial?.primary_goal_other || "");
    const [concerns, setConcerns] = useState(initial?.concerns || "");
    const [timeline, setTimeline] = useState(initial?.timeline || "");
    const [eventDate, setEventDate] = useState(initial?.timeline_event_date || "");
    const [eventName, setEventName] = useState(initial?.timeline_event_name || "");
    const [priorTreatment, setPriorTreatment] = useState(
        initial?.prior_treatment === true ? "yes" : initial?.prior_treatment === false ? "no" : ""
    );
    const [priorNotes, setPriorNotes] = useState(initial?.prior_treatment_notes || "");
    const [saving, setSaving] = useState(false);

    /* Rehydrate local state when the patient (or their existing goals)
       changes — covers the "doctor opens patient A, edits, switches to
       patient B" scenario where the same component instance is reused. */
    useEffect(() => {
        setPrimaryGoal(initial?.primary_goal || "");
        setPrimaryGoalOther(initial?.primary_goal_other || "");
        setConcerns(initial?.concerns || "");
        setTimeline(initial?.timeline || "");
        setEventDate(initial?.timeline_event_date || "");
        setEventName(initial?.timeline_event_name || "");
        setPriorTreatment(initial?.prior_treatment === true ? "yes" : initial?.prior_treatment === false ? "no" : "");
        setPriorNotes(initial?.prior_treatment_notes || "");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initial?.primary_goal, initial?.primary_goal_other, initial?.concerns, initial?.timeline, initial?.timeline_event_date, initial?.timeline_event_name, initial?.prior_treatment, initial?.prior_treatment_notes, patientId]);

    const handleSave = async () => {
        if (!primaryGoal) {
            toast.error("Please choose a primary goal");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                primary_goal: primaryGoal,
                primary_goal_other: primaryGoal === "other" ? primaryGoalOther : "",
                concerns: concerns.trim(),
                timeline: timeline || null,
                timeline_event_date: timeline === "specific_event" ? eventDate : null,
                timeline_event_name: timeline === "specific_event" ? eventName.trim() : "",
                prior_treatment: priorTreatment === "yes" ? true : priorTreatment === "no" ? false : null,
                prior_treatment_notes: priorTreatment === "yes" ? priorNotes.trim() : "",
            };
            const r = await api.put(`/doctor/patients/${patientId}/smile-goals`, payload);
            toast.success("Smile goals saved");
            onSaved?.(r.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't save smile goals");
        } finally {
            setSaving(false);
        }
    };

    void patientName; // header copy now lives in the wrapping SectionCard
    return (
        <div className="space-y-4" data-testid="smile-goals-editor">
            {/* Primary goal */}
            <div>
                <Label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2 block">
                    Primary goal *
                </Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {Object.entries(SMILE_GOAL_META).map(([key, meta]) => {
                        const Icon = meta.Icon;
                        const active = primaryGoal === key;
                        return (
                            <button
                                key={key}
                                type="button"
                                onClick={() => setPrimaryGoal(key)}
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-left text-[12.5px] font-medium transition ${
                                    active
                                        ? "border-primary bg-primary/5 text-primary ring-2 ring-primary/30"
                                        : "border-border hover:border-primary/30 hover:bg-secondary/40"
                                }`}
                                data-testid={`smile-goals-primary-${key}`}
                            >
                                <Icon size={14} weight="fill" className={active ? "text-primary" : "text-muted-foreground"} />
                                {meta.label}
                            </button>
                        );
                    })}
                </div>
                {primaryGoal === "other" && (
                    <Input
                        value={primaryGoalOther}
                        onChange={(e) => setPrimaryGoalOther(e.target.value)}
                        placeholder="e.g. fix overbite for sleep apnea"
                        className="mt-2 text-sm"
                        data-testid="smile-goals-primary-other"
                    />
                )}
            </div>

            {/* Concerns */}
            <div>
                <Label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2 block">
                    What bothers them most
                </Label>
                <Textarea
                    value={concerns}
                    onChange={(e) => setConcerns(e.target.value)}
                    placeholder="In their own words — e.g. crowding in lower front teeth, gap between upper front teeth…"
                    rows={2}
                    className="text-sm"
                    maxLength={300}
                    data-testid="smile-goals-concerns-input"
                />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
                {/* Timeline */}
                <div>
                    <Label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2 block">
                        Timeline
                    </Label>
                    <div className="grid grid-cols-2 gap-2">
                        {Object.entries(SMILE_TIMELINE_META).map(([key, label]) => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => setTimeline(key)}
                                className={`px-3 py-2 rounded-lg border text-left text-[12px] font-medium transition ${
                                    timeline === key
                                        ? "border-primary bg-primary/5 text-primary ring-2 ring-primary/30"
                                        : "border-border hover:border-primary/30 hover:bg-secondary/40"
                                }`}
                                data-testid={`smile-goals-timeline-${key}`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                    {timeline === "specific_event" && (
                        <div className="mt-2 space-y-2">
                            <Input
                                value={eventName}
                                onChange={(e) => setEventName(e.target.value)}
                                placeholder="What's the event? e.g. Wedding, Graduation, Job interview"
                                className="text-sm"
                                maxLength={80}
                                data-testid="smile-goals-event-name"
                            />
                            <Input
                                type="date"
                                value={eventDate}
                                onChange={(e) => setEventDate(e.target.value)}
                                className="text-sm"
                                data-testid="smile-goals-event-date"
                            />
                        </div>
                    )}
                </div>

                {/* Prior treatment */}
                <div>
                    <Label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2 block">
                        Prior ortho treatment
                    </Label>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => setPriorTreatment("no")}
                            className={`flex-1 px-3 py-2 rounded-lg border text-[12.5px] font-medium transition ${
                                priorTreatment === "no"
                                    ? "border-primary bg-primary/5 text-primary ring-2 ring-primary/30"
                                    : "border-border hover:border-primary/30 hover:bg-secondary/40"
                            }`}
                            data-testid="smile-goals-prior-no"
                        >
                            First time
                        </button>
                        <button
                            type="button"
                            onClick={() => setPriorTreatment("yes")}
                            className={`flex-1 px-3 py-2 rounded-lg border text-[12.5px] font-medium transition ${
                                priorTreatment === "yes"
                                    ? "border-primary bg-primary/5 text-primary ring-2 ring-primary/30"
                                    : "border-border hover:border-primary/30 hover:bg-secondary/40"
                            }`}
                            data-testid="smile-goals-prior-yes"
                        >
                            Had braces/aligners
                        </button>
                    </div>
                    {priorTreatment === "yes" && (
                        <Input
                            value={priorNotes}
                            onChange={(e) => setPriorNotes(e.target.value)}
                            placeholder="Brief detail — e.g. metal braces 10 yrs ago, relapsed"
                            className="mt-2 text-sm"
                            data-testid="smile-goals-prior-notes"
                        />
                    )}
                </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
                <Button type="button" onClick={handleSave} disabled={saving || !primaryGoal} data-testid="smile-goals-editor-save">
                    {saving ? (
                        <><Spinner size={14} className="mr-1.5 animate-spin" /> Saving…</>
                    ) : (
                        <><FloppyDisk size={14} weight="duotone" className="mr-1.5" /> Save smile goals</>
                    )}
                </Button>
            </div>
        </div>
    );
}


/* ---------------- Tab: Details (demographic + medical history editor) ---------------- */
const GENDER_OPTIONS = [
    { value: "", label: "Not set" },
    { value: "male", label: "Male" },
    { value: "female", label: "Female" },
    { value: "non_binary", label: "Non-binary" },
    { value: "prefer_not_to_say", label: "Prefer not to say" },
];
/* iter98.99dn — Legacy patient records (created before the value set
   was unified) stored gender as the human-readable label ("Male",
   "Female", "Other", "Prefer not to say") instead of the canonical
   snake_case value. Map them back so the dropdown reflects the saved
   value instead of falling back to "Not set". */
const normaliseGender = (raw) => {
    if (!raw) return "";
    const v = String(raw).trim().toLowerCase();
    if (v === "male" || v === "m") return "male";
    if (v === "female" || v === "f") return "female";
    if (v === "non_binary" || v === "nonbinary" || v === "non-binary" || v === "other") return "non_binary";
    if (v === "prefer_not_to_say" || v === "prefer not to say") return "prefer_not_to_say";
    return v;
};
const BLOOD_GROUPS = ["", "A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

function DetailsTab({ patient, onSaved }) {
    const buildInitial = (p) => ({
        name: p.name || "", email: p.email || "",
        dob: p.dob || "", age: p.age ?? "", gender: normaliseGender(p.gender),
        address: p.address || "", blood_group: p.blood_group || "",
        medical_history: p.medical_history || "",
        allergies: p.allergies || "",
        current_medications: p.current_medications || "",
        emergency_contact_name: p.emergency_contact_name || "",
        emergency_contact_phone: to10Digit(p.emergency_contact_phone || ""),
        notes: p.notes || "",
    });
    const [form, setForm] = useState(buildInitial(patient));
    const [original, setOriginal] = useState(buildInitial(patient));
    const [busy, setBusy] = useState(false);

    const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const buildPayload = () => {
        // age must be int|null on the backend — handle it FIRST so it doesn't
        // fall into the generic "string → trim" branch (which would forward
        // empty strings unchanged and trigger a 422).
        const out = {};
        Object.entries(form).forEach(([k, v]) => {
            if (k === "age") {
                const s = typeof v === "string" ? v.trim() : v;
                if (s === "" || s == null) {
                    out[k] = null;
                } else {
                    const n = Number(s);
                    out[k] = Number.isFinite(n) ? n : null;
                }
            } else if (k === "emergency_contact_phone") {
                // Convert 10-digit UI value to E.164 for storage.
                const trimmed = typeof v === "string" ? v.trim() : "";
                out[k] = trimmed ? (toE164(trimmed) || trimmed) : "";
            } else if (typeof v === "string") {
                out[k] = v.trim();
            } else {
                out[k] = v;
            }
        });
        return out;
    };

    const save = async () => {
        if (!form.name || form.name.trim().length < 2) {
            toast.error("Name is required"); return;
        }
        setBusy(true);
        try {
            await api.put(`/doctor/patients/${patient.id}`, buildPayload());
            toast.success("Patient details updated");
            const next = buildInitial({ ...patient, ...form });
            setOriginal(next);
            onSaved?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't update patient");
        } finally { setBusy(false); }
    };

    const cancel = () => setForm(original);
    const isDirty = JSON.stringify(form) !== JSON.stringify(original);

    return (
        // iter101.82 — Full-width tab (removed max-w-4xl).
        <div data-testid="patient-details-tab" className="space-y-6">
            <SectionCard title="Personal" icon={<Info size={14} weight="bold" />}>
                <div className="grid sm:grid-cols-2 gap-4">
                    <DetailField label="Full name *">
                        <Input value={form.name} onChange={(e) => upd("name", e.target.value)} className="h-10" data-testid="patient-details-name" />
                    </DetailField>
                    <DetailField label="Phone" hint="Read-only (auth identifier)">
                        <Input value={patient.phone || ""} disabled className="h-10" />
                    </DetailField>
                    <DetailField label="Email">
                        <Input value={form.email} onChange={(e) => upd("email", e.target.value)} type="email" className="h-10" data-testid="patient-details-email" />
                    </DetailField>
                    <DetailField label="Date of birth">
                        <Input value={form.dob} onChange={(e) => upd("dob", e.target.value)} type="date" className="h-10" data-testid="patient-details-dob" />
                    </DetailField>
                    <DetailField label="Age" hint="Optional; computed from DOB if blank">
                        <Input
                            value={form.age}
                            onChange={(e) => upd("age", e.target.value.replace(/[^0-9]/g, ""))}
                            inputMode="numeric" className="h-10" data-testid="patient-details-age"
                        />
                    </DetailField>
                    <DetailField label="Gender">
                        <select
                            value={form.gender}
                            onChange={(e) => upd("gender", e.target.value)}
                            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                            data-testid="patient-details-gender"
                        >
                            {GENDER_OPTIONS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                        </select>
                    </DetailField>
                </div>
                <DetailField label="Address">
                    <Textarea rows={2} value={form.address} onChange={(e) => upd("address", e.target.value)} data-testid="patient-details-address" />
                </DetailField>
            </SectionCard>

            <SectionCard title="Medical" icon={<FirstAid size={14} weight="bold" />}>
                <div className="grid sm:grid-cols-2 gap-4">
                    <DetailField label="Blood group">
                        <select
                            value={form.blood_group}
                            onChange={(e) => upd("blood_group", e.target.value)}
                            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                            data-testid="patient-details-blood-group"
                        >
                            {BLOOD_GROUPS.map((b) => <option key={b} value={b}>{b || "Not set"}</option>)}
                        </select>
                    </DetailField>
                    <DetailField label="Allergies" hint="Comma separated">
                        <Input value={form.allergies} onChange={(e) => upd("allergies", e.target.value)} placeholder="e.g. Penicillin, latex" data-testid="patient-details-allergies" className="h-10" />
                    </DetailField>
                </div>
                <DetailField label="Current medications">
                    <Textarea rows={2} value={form.current_medications} onChange={(e) => upd("current_medications", e.target.value)} placeholder="List any drugs the patient is on (with dosage if known)" data-testid="patient-details-medications" />
                </DetailField>
                <DetailField label="Medical history">
                    <Textarea rows={3} value={form.medical_history} onChange={(e) => upd("medical_history", e.target.value)} placeholder="Notable conditions: diabetes, hypertension, prior orthodontic treatment, etc." data-testid="patient-details-history" />
                </DetailField>
            </SectionCard>

            <SectionCard title="Emergency contact" icon={<Warning size={14} weight="bold" />}>
                <div className="grid sm:grid-cols-2 gap-4">
                    <DetailField label="Contact name">
                        <Input value={form.emergency_contact_name} onChange={(e) => upd("emergency_contact_name", e.target.value)} placeholder="Full name" className="h-10" data-testid="patient-details-ec-name" />
                    </DetailField>
                    <DetailField label="Contact phone">
                        <PhoneInput10
                            value={form.emergency_contact_phone}
                            onChange={(v) => upd("emergency_contact_phone", v)}
                            inputClassName="h-10"
                            testid="patient-details-ec-phone"
                        />
                    </DetailField>
                </div>
            </SectionCard>

            <SectionCard title="Internal notes" icon={<PencilSimple size={14} weight="bold" />}>
                <DetailField label="Notes" hint="Only visible to your clinic">
                    <Textarea rows={3} value={form.notes} onChange={(e) => upd("notes", e.target.value)} placeholder="Internal notes — never shown to the patient" data-testid="patient-details-notes" />
                </DetailField>
            </SectionCard>

            {/* iter98.99dn — Smile goals capture lives on the About tab
                alongside the rest of the patient-detail fields. The
                Overview tab now only displays the goals in a compact
                strip. Saves via PUT /api/doctor/patients/{id}/smile-goals
                independently of the rest of the form (its own Save
                button below), so the doctor doesn't lose data if they
                forget to hit "Save changes" at the bottom of the page. */}
            <SectionCard title="Smile goals" icon={<Smiley size={14} weight="bold" />}>
                <DoctorSmileGoalsEditor
                    patientId={patient.id}
                    patientName={patient.name}
                    initial={patient.smile_goals}
                    onSaved={() => onSaved?.()}
                />
            </SectionCard>

            <div className="sticky bottom-4 flex items-center justify-end gap-2 bg-background/90 backdrop-blur border border-border rounded-full px-4 py-3 shadow-sm">
                <span className="text-[11px] font-bold uppercase tracking-wider mr-auto">
                    {isDirty
                        ? <span className="text-amber-700">Unsaved changes</span>
                        : <span className="text-muted-foreground">All saved</span>}
                </span>
                <Button variant="ghost" onClick={cancel} disabled={busy || !isDirty} data-testid="patient-details-cancel">Discard</Button>
                <Button onClick={save} disabled={busy || !isDirty} className="rounded-full" data-testid="patient-details-save">
                    {busy ? "Saving…" : "Save changes"}
                </Button>
            </div>
        </div>
    );
}

function SectionCard({ title, icon, children }) {
    return (
        <div className="rounded-2xl border border-border bg-card p-5 space-y-4">
            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground border-b border-border/60 pb-1.5 flex items-center gap-1.5">
                {icon} {title}
            </div>
            {children}
        </div>
    );
}

function DetailField({ label, hint, children }) {
    return (
        <div>
            <Label className="text-xs font-semibold flex items-center gap-1.5">
                {label}
                {hint && <span className="text-[10px] font-normal text-muted-foreground italic">· {hint}</span>}
            </Label>
            <div className="mt-1">{children}</div>
        </div>
    );
}

/* ---------------- Tab: Payments (ledger across all of the patient's cases) ---------------- */
const PAYMENT_MODES = [
    { value: "cash", label: "Cash" },
    { value: "upi", label: "UPI" },
    { value: "card", label: "Card" },
    { value: "bank_transfer", label: "Bank transfer" },
    { value: "cheque", label: "Cheque" },
    { value: "other", label: "Other" },
];

function PaymentsTab({ patientId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeAddCase, setActiveAddCase] = useState(null); // case_id
    const [editingQuoteCase, setEditingQuoteCase] = useState(null);

    const load = () => {
        setLoading(true);
        api.get(`/doctor/patients/${patientId}/payments`)
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [patientId]);

    if (loading || !data) {
        return <div className="text-center text-muted-foreground py-12 text-sm"><Spinner size={18} className="inline-block animate-spin mr-2" />Loading payments…</div>;
    }
    if (data.sections.length === 0) {
        return (
            <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center">
                <Wallet size={28} className="mx-auto text-muted-foreground mb-3" />
                <div className="font-display text-lg font-bold">No cases yet</div>
                <p className="text-xs text-muted-foreground mt-1.5">Payments are tracked per case. Submit a new case for this patient to start logging payments.</p>
            </div>
        );
    }

    return (
        // iter101.82 — Full-width tab (removed max-w-4xl).
        <div data-testid="patient-payments-tab" className="space-y-6">
            <PaymentsSummaryStrip summary={data.summary} />
            {data.sections.map((s) => (
                <CasePaymentSection
                    key={s.case_id}
                    section={s}
                    isAdding={activeAddCase === s.case_id}
                    onOpenAdd={() => setActiveAddCase(s.case_id)}
                    onCloseAdd={() => setActiveAddCase(null)}
                    isEditingQuote={editingQuoteCase === s.case_id}
                    onOpenQuote={() => setEditingQuoteCase(s.case_id)}
                    onCloseQuote={() => setEditingQuoteCase(null)}
                    onChanged={load}
                />
            ))}
        </div>
    );
}

function PaymentsSummaryStrip({ summary }) {
    const pct = summary.quote_total > 0
        ? Math.min(100, Math.round((summary.paid_total / summary.quote_total) * 100))
        : 0;
    return (
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="payments-summary-strip">
            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground mb-3">
                All-cases payment summary
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Stat label="Cases" value={summary.cases_count} />
                <Stat label="Quoted" value={fmtINR(summary.quote_total)} />
                <Stat label="Paid" value={fmtINR(summary.paid_total)} tone="emerald" />
                <Stat label="Balance" value={fmtINR(summary.balance)} tone="amber" />
            </div>
            {summary.quote_total > 0 && (
                <div className="mt-4 space-y-1">
                    <div className="h-1.5 w-full bg-border rounded-full overflow-hidden">
                        <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="text-[10px] text-muted-foreground text-right tabular-nums">{pct}% paid overall</div>
                </div>
            )}
        </div>
    );
}

function Stat({ label, value, tone }) {
    const toneClass = tone === "emerald" ? "text-emerald-700"
        : tone === "amber" ? "text-amber-700"
        : "text-foreground";
    return (
        <div>
            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground mb-1">{label}</div>
            <div className={`text-lg font-bold tabular-nums ${toneClass}`}>{value}</div>
        </div>
    );
}

function CasePaymentSection({ section, isAdding, onOpenAdd, onCloseAdd, isEditingQuote, onOpenQuote, onCloseQuote, onChanged }) {
    const pct = section.quote_total > 0
        ? Math.min(100, Math.round((section.paid_total / section.quote_total) * 100))
        : 0;
    return (
        <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid={`case-payment-section-${section.case_id}`}>
            <div className="p-5 border-b border-border bg-secondary/20">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="min-w-0">
                        <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Case</div>
                        <div className="flex items-baseline gap-2">
                            <div className="font-mono text-lg font-bold text-primary">{section.case_code}</div>
                            <div className="text-xs text-muted-foreground truncate">{section.diagnosis}</div>
                        </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                        <Button variant="ghost" size="sm" onClick={onOpenQuote} data-testid={`case-payment-edit-quote-${section.case_id}`}>
                            <PencilSimple size={12} weight="bold" className="mr-1.5" /> {section.quote_total > 0 ? "Edit quote" : "Add quote"}
                        </Button>
                        <Button size="sm" onClick={onOpenAdd} data-testid={`case-payment-add-${section.case_id}`}>
                            <Plus size={12} weight="bold" className="mr-1.5" /> Log payment
                        </Button>
                    </div>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-4">
                    <Stat label="Quoted" value={fmtINR(section.quote_total)} />
                    <Stat label="Paid" value={fmtINR(section.paid_total)} tone="emerald" />
                    <Stat label="Balance" value={fmtINR(section.balance)} tone="amber" />
                </div>
                {section.quote_total > 0 && (
                    <div className="mt-3 h-1.5 w-full bg-border rounded-full overflow-hidden">
                        <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                    </div>
                )}
            </div>

            {/* Inline ADD form */}
            {isAdding && (
                <AddPaymentForm
                    caseId={section.case_id}
                    suggestedAmount={section.balance > 0 ? section.balance : ""}
                    onClose={onCloseAdd}
                    onSaved={() => { onCloseAdd(); onChanged(); }}
                />
            )}

            {/* Inline QUOTE form */}
            {isEditingQuote && (
                <QuoteEditForm
                    caseId={section.case_id}
                    initial={section.quote_total}
                    onClose={onCloseQuote}
                    onSaved={() => { onCloseQuote(); onChanged(); }}
                />
            )}

            {/* Ledger entries */}
            <div className="divide-y divide-border">
                {section.payments.length === 0 ? (
                    <div className="text-center py-6 text-xs text-muted-foreground italic">No payments logged yet</div>
                ) : section.payments.map((p) => (
                    <PaymentLedgerRow key={p.id} caseId={section.case_id} payment={p} onDeleted={onChanged} />
                ))}
            </div>
        </div>
    );
}

function AddPaymentForm({ caseId, suggestedAmount, onClose, onSaved }) {
    const [amount, setAmount] = useState(suggestedAmount !== "" ? String(suggestedAmount) : "");
    const [mode, setMode] = useState("upi");
    const [reference, setReference] = useState("");
    const [note, setNote] = useState("");
    const [paidOn, setPaidOn] = useState(() => new Date().toISOString().slice(0, 10));
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        const amt = Number(amount);
        if (!amt || amt <= 0) { toast.error("Enter an amount"); return; }
        setBusy(true);
        try {
            await api.post(`/cases/${caseId}/payments`, {
                amount: amt, mode, paid_on: paidOn, reference: reference.trim(), note: note.trim(),
            });
            toast.success(`Payment of ${fmtINR(amt)} logged`);
            onSaved();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't log payment");
        } finally { setBusy(false); }
    };

    return (
        <div className="border-b border-border bg-primary/5 p-5 grid grid-cols-1 sm:grid-cols-6 gap-3 items-end" data-testid="add-payment-form">
            <div className="sm:col-span-2">
                <Label className="text-xs font-semibold">Amount (₹) *</Label>
                <Input
                    value={amount}
                    onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, ""))}
                    inputMode="numeric"
                    className="h-9 mt-1 tabular-nums"
                    data-testid="add-payment-amount"
                />
            </div>
            <div>
                <Label className="text-xs font-semibold">Mode</Label>
                <select
                    value={mode} onChange={(e) => setMode(e.target.value)}
                    className="h-9 w-full mt-1 rounded-md border border-input bg-background px-2 text-sm"
                    data-testid="add-payment-mode"
                >
                    {PAYMENT_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
            </div>
            <div>
                <Label className="text-xs font-semibold">Paid on</Label>
                <Input value={paidOn} onChange={(e) => setPaidOn(e.target.value)} type="date" className="h-9 mt-1" data-testid="add-payment-paid-on" />
            </div>
            <div className="sm:col-span-2">
                <Label className="text-xs font-semibold">Reference / receipt</Label>
                <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="UPI ref / receipt no." className="h-9 mt-1" data-testid="add-payment-reference" />
            </div>
            <div className="sm:col-span-5">
                <Label className="text-xs font-semibold">Note</Label>
                <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything to remember about this payment" className="h-9 mt-1" data-testid="add-payment-note" />
            </div>
            <div className="flex gap-2 justify-end sm:col-span-6">
                <Button variant="ghost" size="sm" onClick={onClose} disabled={busy} data-testid="add-payment-cancel">Cancel</Button>
                <Button size="sm" onClick={submit} disabled={busy} data-testid="add-payment-submit">
                    {busy ? "Saving…" : "Log payment"}
                </Button>
            </div>
        </div>
    );
}

function QuoteEditForm({ caseId, initial, onClose, onSaved }) {
    const [value, setValue] = useState(initial > 0 ? String(initial) : "");
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        const v = Number(value) || 0;
        setBusy(true);
        try {
            await api.put(`/cases/${caseId}/quote`, { patient_quote_total: v });
            toast.success(`Quote updated to ${fmtINR(v)}`);
            onSaved();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't update quote");
        } finally { setBusy(false); }
    };

    return (
        <div className="border-b border-border bg-primary/5 p-5 flex items-end gap-3" data-testid="edit-quote-form">
            <div className="flex-1 max-w-xs">
                <Label className="text-xs font-semibold">Patient-quoted total (₹)</Label>
                <Input
                    value={value}
                    onChange={(e) => setValue(e.target.value.replace(/[^0-9]/g, ""))}
                    inputMode="numeric"
                    placeholder="e.g. 75000"
                    className="h-9 mt-1 tabular-nums"
                    data-testid="edit-quote-input"
                />
                <p className="text-[10px] text-muted-foreground mt-1">Your clinic's full price to the patient (not the lab charge).</p>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} disabled={busy} data-testid="edit-quote-cancel">Cancel</Button>
            <Button size="sm" onClick={submit} disabled={busy} data-testid="edit-quote-submit">
                {busy ? "Saving…" : "Save quote"}
            </Button>
        </div>
    );
}

function PaymentLedgerRow({ caseId, payment, onDeleted }) {
    const [busy, setBusy] = useState(false);
    const modeLabel = (PAYMENT_MODES.find((m) => m.value === payment.mode) || {}).label || payment.mode;

    const doDelete = async () => {
        // eslint-disable-next-line no-alert
        if (!(await confirmDialog({ title: "Delete this payment entry? This can't be undone.", confirmText: "Delete", tone: "danger" }))) return;
        setBusy(true);
        try {
            await api.delete(`/cases/${caseId}/payments/${payment.id}`);
            toast.success("Payment removed");
            onDeleted();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't remove payment");
        } finally { setBusy(false); }
    };

    return (
        <div className="px-5 py-3 grid grid-cols-12 gap-3 items-center hover:bg-secondary/30 text-sm" data-testid={`payment-row-${payment.id}`}>
            <div className="col-span-3 tabular-nums text-xs text-muted-foreground">
                {payment.paid_on || (payment.created_at || "").slice(0, 10)}
            </div>
            <div className="col-span-2 tabular-nums font-semibold text-emerald-700">{fmtINR(payment.amount)}</div>
            <div className="col-span-2 text-xs uppercase font-bold tracking-wider text-muted-foreground">{modeLabel}</div>
            <div className="col-span-4 text-xs text-foreground/85 truncate">
                {payment.reference && <span className="font-mono">{payment.reference}</span>}
                {payment.reference && payment.note ? " · " : ""}
                {payment.note}
            </div>
            <div className="col-span-1 text-right">
                <button
                    type="button" onClick={doDelete} disabled={busy}
                    className="text-muted-foreground hover:text-rose-600 transition disabled:opacity-50"
                    title="Delete payment"
                    data-testid={`payment-row-delete-${payment.id}`}
                >
                    <Trash size={13} weight="bold" />
                </button>
            </div>
        </div>
    );
}


/* ---------------- Overview card: Patient Nudges ----------------
 * iter98.0 — Smart-template patient nudge widget. Absorbs the
 * functionality of the (now-removed) Nudges tab:
 *
 *   • Template picker auto-selects the message that fits the most
 *     recent case status (records pending, scan reminder, plan
 *     ready, wear-time check-in, retainer follow-up, etc.). Doctors
 *     can edit the message before sending.
 *   • One-click WhatsApp send. Pre-logs to /doctor/nudges so the
 *     entry appears in history immediately.
 *   • Inline history — 2 most-recent rows by default, expand to
 *     full list with the "Show all (N)" toggle. Same row design as
 *     the old Nudges tab used.
 */

/* Template catalogue. Each key maps to a builder that takes a small
 * context object and returns the WhatsApp message. The default
 * picker maps a case status → template key. */
const NUDGE_TEMPLATES = [
    // iter98.99dl — Welcome at the top so it's the first option a doctor
    // sees on a brand-new patient profile. `pickDefaultTemplate` returns
    // this key when the patient has no case yet OR the case is still a
    // draft, so the textarea auto-fills with the welcome copy.
    { key: "welcome",            label: "Welcome new patient" },
    { key: "wear_time",          label: "Wear-time check-in" },
    { key: "records_pending",    label: "Records pending" },
    { key: "scan_reminder",      label: "Scan visit reminder" },
    { key: "scans_received",     label: "Scans received — plan coming" },
    { key: "plan_in_progress",   label: "Plan in progress" },
    { key: "plan_ready",         label: "Treatment plan ready" },
    { key: "aligners_production",label: "Aligners in production" },
    { key: "aligners_shipped",   label: "Aligners shipped" },
    { key: "refinement",         label: "Refinement needed" },
    { key: "retainer",           label: "Retainer check-in" },
    { key: "completed",          label: "Treatment complete" },
    { key: "payment",            label: "Payment follow-up" },
    { key: "appointment",        label: "Appointment reminder" },
    { key: "general",            label: "General check-in" },
];

const TEMPLATE_BUILDERS = {
    /* iter98.99dl — Welcome template fires for brand-new patients (no
       case yet) and gently invites them to set their smile goals on the
       patient portal — pairs with the new doctor-side smile-goals editor
       so by the time the patient logs in, the doctor already knows what
       they want. */
    welcome: ({ first }) =>
        `Hi ${first}, lovely to have you on board! We've set up your patient record at Invisique™ — you can sign in to track everything from your smile goals to aligner progress. Looking forward to our treatment journey together.`,
    wear_time: ({ first, caseCode, alignerStage, totalAligners }) => {
        const progress =
            alignerStage && totalAligners
                ? `aligner #${alignerStage} of ${totalAligners}`
                : "your aligners";
        return `Hi ${first}, just checking in on ${progress}${caseCode ? ` (case ${caseCode})` : ""}. Aim for 22 hours/day for the best result — and let me know straight away if anything feels off.`;
    },
    records_pending: ({ first }) =>
        `Hi ${first}, we're still waiting on a few records (scans / photos / X-rays) to start your aligner plan. Can we book a quick slot this week to get them done?`,
    scan_reminder: ({ first, scanDate }) =>
        `Hi ${first}, this is a reminder of your scheduled scan visit${scanDate ? ` on ${scanDate}` : ""}. Looking forward to seeing you — please be on time!`,
    scans_received: ({ first }) =>
        `Hi ${first}, your scans are with our planning team. I'll share your treatment plan as soon as it lands — usually within 3-4 working days.`,
    plan_in_progress: ({ first }) =>
        `Hi ${first}, quick update — your aligner plan is being prepared by our planning team. I'll loop you in the moment it's ready for review.`,
    plan_ready: ({ first }) =>
        `Hi ${first}, great news — your treatment plan is ready! Let's book a quick call so I can walk you through it before we kick off production.`,
    aligners_production: ({ first }) =>
        `Hi ${first}, your aligners are now in production at our lab. I'll let you know the moment they ship out.`,
    aligners_shipped: ({ first }) =>
        `Hi ${first}, your aligners are on their way! Once they arrive, drop by the clinic so we can fit them properly and walk through wear & care.`,
    refinement: ({ first, caseCode }) =>
        `Hi ${first}, your case${caseCode ? ` (${caseCode})` : ""} needs a refinement to finish the job — let's book a slot to take fresh scans and align the final result.`,
    retainer: ({ first }) =>
        `Hi ${first}, how is the retainer feeling? Consistent wear right now is what locks in your new smile — make sure it's on every night.`,
    completed: ({ first }) =>
        `Hi ${first}, hope you're loving the new smile! Quick reminder — please keep wearing your retainer at night so things stay exactly where they are.`,
    payment: ({ first, caseCode }) =>
        `Hi ${first}, friendly reminder about your outstanding balance${caseCode ? ` on ${caseCode}` : ""}. Let me know if you'd like help with payment options.`,
    appointment: ({ first }) =>
        `Hi ${first}, this is a reminder about your upcoming appointment with us. Please reply to confirm — looking forward to seeing you!`,
    general: ({ first, caseCode }) =>
        `Hi ${first}, just checking in on your treatment${caseCode ? ` (${caseCode})` : ""}. Let me know if you have any questions — happy to help.`,
};

/* Case status → default template key. Unknown statuses fall back to
 * "general" so the doctor still gets a usable message. */
const STATUS_TO_TEMPLATE = {
    draft:              "records_pending",
    scan_visit_scheduled: "scan_reminder",
    scan_visit_completed: "scans_received",
    submitted:          "plan_in_progress",
    in_planning:        "plan_in_progress",
    plan_ready:         "plan_ready",
    doctor_review:      "plan_ready",
    approved:           "aligners_production",
    in_production:     "aligners_production",
    shipped:            "aligners_shipped",
    delivered:          "wear_time",
    refinement:         "refinement",
    retainer:           "retainer",
    completed:          "completed",
};

function pickDefaultTemplate(latestCase) {
    /* iter98.99dl — Brand-new patients with NO case (e.g. just created
       by the doctor) get the welcome template. Drafts also get welcome
       because the patient hasn't been told anything actionable yet. */
    if (!latestCase) return "welcome";
    if (latestCase.status === "draft") return "welcome";
    return STATUS_TO_TEMPLATE[latestCase.status] || "general";
}

function PatientNudgesOverviewCard({ patientId, patientName, patientPhone, latestCase }) {
    const [rows, setRows] = useState(null);
    const [showAll, setShowAll] = useState(false);
    const [templateKey, setTemplateKey] = useState(() => pickDefaultTemplate(latestCase));
    const [message, setMessage] = useState("");
    // Tracks whether the doctor has typed into the textarea. When true
    // we stop auto-updating the message on template change so we don't
    // overwrite their edits. The "Reset to template" link clears it.
    const [edited, setEdited] = useState(false);

    const reload = () => {
        api.get(`/doctor/patients/${patientId}/nudges`)
            .then((r) => setRows(r.data || []))
            .catch(() => setRows([]));
    };

    useEffect(() => {
        if (!patientId) return;
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patientId]);

    const firstName = (patientName || "there").split(" ")[0];
    const caseCode = latestCase?.code || null;
    const ctx = {
        first: firstName,
        caseCode,
        alignerStage: latestCase?.current_aligner_stage || latestCase?.aligner_stage,
        totalAligners: latestCase?.total_aligners || latestCase?.aligner_count_total,
        scanDate: latestCase?.scan_visit_date,
    };

    // Re-build the message when the template changes (unless the doctor
    // has manually edited — see `edited` guard).
    useEffect(() => {
        if (edited) return;
        const builder = TEMPLATE_BUILDERS[templateKey] || TEMPLATE_BUILDERS.general;
        setMessage(builder(ctx));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateKey, latestCase?.id, latestCase?.status, latestCase?.code, patientName, edited]);

    const phoneBlock = waReason(patientPhone);
    const wa = waLink(patientPhone, message);

    const onSendNudge = () => {
        api.post("/doctor/nudges", {
            patient_id: patientId,
            case_id: latestCase?.id || null,
            reason: "manual_nudge",
            message,
            channel: "whatsapp",
        })
            .then(() => reload())
            .catch(() => { /* non-critical */ });
    };

    const onResetTemplate = () => {
        setEdited(false);
        const builder = TEMPLATE_BUILDERS[templateKey] || TEMPLATE_BUILDERS.general;
        setMessage(builder(ctx));
    };

    const total = rows?.length || 0;
    const visible = showAll ? (rows || []) : (rows || []).slice(0, 2);
    const defaultTemplateKey = pickDefaultTemplate(latestCase);
    const defaultTemplate = NUDGE_TEMPLATES.find((t) => t.key === defaultTemplateKey);
    const lastSentAgo = rows?.[0]?.sent_at ? fmtAgo(rows[0].sent_at) : null;

    return (
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="overview-nudges-card">
            {/* Header */}
            <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex items-start gap-2.5">
                    <span className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-700 grid place-items-center shrink-0">
                        <WhatsappLogo size={17} weight="duotone" />
                    </span>
                    <div>
                        <div className="font-display text-base font-bold leading-tight">Nudges</div>
                        <div className="text-[11.5px] text-muted-foreground mt-0.5">
                            {total > 0
                                ? `${total} outreach ${total === 1 ? "log" : "logs"}${lastSentAgo ? ` · last ${lastSentAgo}` : ""}`
                                : "Quick patient outreach via WhatsApp"}
                        </div>
                    </div>
                </div>
            </div>

            {/* Composer */}
            <div className="rounded-xl border border-border bg-secondary/30 p-3.5 space-y-2.5" data-testid="overview-nudges-composer">
                <div className="flex items-center gap-2 flex-wrap">
                    <Label className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                        Template
                    </Label>
                    <select
                        value={templateKey}
                        onChange={(e) => { setTemplateKey(e.target.value); setEdited(false); }}
                        className="h-8 text-xs rounded-md border border-border bg-card px-2.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                        data-testid="overview-nudges-template-select"
                    >
                        {NUDGE_TEMPLATES.map((t) => (
                            <option key={t.key} value={t.key}>
                                {t.label}{t.key === defaultTemplateKey ? " · suggested" : ""}
                            </option>
                        ))}
                    </select>
                    {defaultTemplate && templateKey !== defaultTemplateKey && (
                        <button
                            type="button"
                            onClick={() => { setTemplateKey(defaultTemplateKey); setEdited(false); }}
                            className="text-[10.5px] font-semibold text-primary hover:underline"
                        >
                            Use suggested ({defaultTemplate.label})
                        </button>
                    )}
                    {edited && (
                        <button
                            type="button"
                            onClick={onResetTemplate}
                            className="text-[10.5px] font-semibold text-muted-foreground hover:text-foreground"
                        >
                            Reset to template
                        </button>
                    )}
                </div>

                <Textarea
                    value={message}
                    onChange={(e) => { setMessage(e.target.value); setEdited(true); }}
                    rows={3}
                    className="text-sm resize-none bg-card"
                    data-testid="overview-nudges-message"
                />

                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <p className="text-[11px] text-muted-foreground">
                        Opens WhatsApp in a new tab. The outreach is logged below for tracking.
                    </p>
                    {wa ? (
                        <a
                            href={wa}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={onSendNudge}
                            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-4 h-9 transition shrink-0"
                            data-testid="overview-nudges-send-btn"
                        >
                            <WhatsappLogo size={13} weight="fill" /> Send via WhatsApp
                        </a>
                    ) : (
                        <span
                            className="text-[10px] text-muted-foreground italic shrink-0"
                            title={phoneBlock || "Phone unavailable"}
                            data-testid="overview-nudges-no-phone"
                        >
                            {phoneBlock || "No phone on file"}
                        </span>
                    )}
                </div>
            </div>

            {/* History */}
            <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">
                        Recent activity
                    </div>
                    {total > 2 && (
                        <button
                            type="button"
                            onClick={() => setShowAll((s) => !s)}
                            className="text-[11px] font-semibold text-primary hover:underline inline-flex items-center gap-1"
                            data-testid="overview-nudges-toggle-all"
                        >
                            {showAll ? "Show less" : `Show all (${total})`}
                            <ArrowRight size={11} weight="bold" className={showAll ? "rotate-90 transition-transform" : "transition-transform"} />
                        </button>
                    )}
                </div>
                {rows === null ? (
                    <div className="text-[11.5px] text-muted-foreground italic py-2">Loading nudge history…</div>
                ) : visible.length === 0 ? (
                    <div className="text-[11.5px] text-muted-foreground leading-snug py-2" data-testid="overview-nudges-empty">
                        No nudges sent yet. Send one above and it&apos;ll land here so you can track what worked.
                    </div>
                ) : (
                    <div className="space-y-2">
                        {visible.map((n) => (
                            <NudgeHistoryRow key={n.id} n={n} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function NudgeHistoryRow({ n }) {
    const channelStyle = {
        whatsapp: { label: "WhatsApp", bg: "bg-emerald-50 text-emerald-800 border-emerald-200" },
        sms: { label: "SMS", bg: "bg-sky-50 text-sky-800 border-sky-200" },
        in_app: { label: "In-app", bg: "bg-violet-50 text-violet-800 border-violet-200" },
        phone: { label: "Phone", bg: "bg-amber-50 text-amber-800 border-amber-200" },
    }[n.channel] || { label: n.channel, bg: "bg-secondary text-foreground" };

    const outcomeStyle = {
        pending: { label: "Pending", bg: "bg-amber-50 text-amber-800 border-amber-200", icon: Clock },
        resolved_auto: { label: "Auto-resolved", bg: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: CheckCircle },
        resolved_manual: { label: "Resolved", bg: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: CheckCircle },
        dismissed: { label: "Dismissed", bg: "bg-secondary text-muted-foreground", icon: X },
    }[n.outcome] || { label: n.outcome, bg: "bg-secondary text-foreground", icon: Clock };

    const OutcomeIcon = outcomeStyle.icon;
    return (
        <div className="bg-card border border-border rounded-xl p-3.5 hover:border-primary/30 transition" data-testid={`nudge-history-${n.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${channelStyle.bg}`}>
                        {channelStyle.label}
                    </span>
                    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${outcomeStyle.bg}`}>
                        <OutcomeIcon size={10} weight="bold" /> {outcomeStyle.label}
                    </span>
                    {n.reason && (
                        <span className="text-[11px] text-muted-foreground">· {n.reason}</span>
                    )}
                </div>
                <span className="text-[11px] text-muted-foreground tabular-nums">
                    {fmtAgo(n.sent_at)}
                </span>
            </div>

            {n.message && (
                <p className="text-xs text-foreground/85 mt-2 leading-relaxed line-clamp-2 whitespace-pre-wrap">
                    &ldquo;{n.message}&rdquo;
                </p>
            )}

            {(n.outcome === "resolved_auto" || n.outcome === "resolved_manual") && n.resolved_reason && (
                <div className="mt-2 text-[11px] text-emerald-700 italic">
                    {n.resolved_reason}
                </div>
            )}

            <div className="text-[10px] text-muted-foreground mt-1.5">
                Sent by {n.doctor_name || "Doctor"}
                {n.case_id && <span className="font-mono"> · case {n.case_id.slice(0, 8)}</span>}
            </div>
        </div>
    );
}

function fmtAgo(iso) {
    if (!iso) return "";
    const ms = Date.now() - new Date(iso).getTime();
    if (Number.isNaN(ms) || ms < 0) return "";
    const mins = Math.round(ms / 60_000);
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs} h ago`;
    const days = Math.round(hrs / 24);
    if (days < 30) return `${days} d ago`;
    const months = Math.round(days / 30);
    return `${months} mo ago`;
}


